import type { IAttribute, IViolation } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { OracleTableDataset } from 'app/model/dataset/oracle-table.dataset';
import { TabularCopySource } from 'app/model/pipeline/copysource/tabular-type.copysource';

import { partitionOptionConst } from 'app/registries/consts/metadata-consts';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

export const DEFAULT_ORACLE_NUMBER_PRECISION = 38;
export const DEFAULT_ORACLE_NUMBER_SCALE = 18;

@Metamodel.model({
    name: adfModelTypeMap.oracle.copySourceType,
    display: [],
    tags: { datasetModel: Metamodel.modelFor(OracleTableDataset) }
})
export class OracleCopySource extends TabularCopySource {
    public static readonly PARTITION_OPTION: IAttribute;

    public static readonly PARTITION_NAMES: IAttribute;
    public static readonly PARTITION_COLUMN_NAME: IAttribute;
    public static readonly PARTITION_UPPER_BOUND: IAttribute;
    public static readonly PARTITION_LOWER_BOUND: IAttribute;
    public static readonly QUERY_TIMEOUT: IAttribute;
    public static readonly CONVERT_DECIMAL_TO_INTEGER: IAttribute;
    public static readonly NUMBER_PRECISION: IAttribute;
    public static readonly NUMBER_SCALE: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'PARTITION_OPTION',
        display: stringResources.ModelName_Partition_Option
    })
    public partitionOption?: string = partitionOptionConst.none;

    @Metamodel.attribute({
        templatable: true,
        association: 'PARTITION_NAMES',
        display: stringResources.ModelName_Partition_Names
    })
    public partitionNames?: string[];

    @Metamodel.attribute({
        templatable: true,
        association: 'PARTITION_COLUMN_NAME',
        display: stringResources.ModelName_Partition_ColumnName
    })
    public partitionColumnName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'PARTITION_UPPER_BOUND',
        display: stringResources.ModelName_Partition_UpperBound
    })
    public partitionUpperBound?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'PARTITION_LOWER_BOUND',
        display: stringResources.ModelName_Partition_LowerBound
    })
    public partitionLowerBound?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'CONVERT_DECIMAL_TO_INTEGER',
        display: stringResources.convertDec2Int
    })
    public convertDecimalToInteger?: boolean;

    @Metamodel.attribute({
        templatable: true,
        association: 'QUERY_TIMEOUT',
        display: stringResources.ModelName_Source_QueryTimeout_Min,
        accepts: ['String'],
        constraint: {
            minimum: 1,
            maximum: 1440
        }
    })
    public queryTimeout?: number = 120;

    @Metamodel.attribute({
        templatable: true,
        association: 'NUMBER_PRECISION',
        display: stringResources.DataFlowColumnType_DecimalPrecision,
        constraint: {
            minimum: 1,
            maximum: 256,
            validator: OracleCopySource.validateNumberPrecision
        }
    })
    public numberPrecision?: number = DEFAULT_ORACLE_NUMBER_PRECISION;

    @Metamodel.attribute({
        templatable: true,
        association: 'NUMBER_SCALE',
        display: stringResources.DataFlowColumnType_Scale,
        constraint: {
            minimum: 0,
            maximum: 130,
            validator: OracleCopySource.validateNumberScale
        }
    })
    public numberScale?: number = DEFAULT_ORACLE_NUMBER_SCALE;

    private static validateNumberPrecision(source: OracleCopySource): IViolation[] {
        // An omitted scale uses the backend default of 130. Zero is an explicit scale.
        if (source.numberPrecision != null &&
            OracleCopySource.NUMBER_PRECISION.templateValue(source) == null &&
            source.numberScale == null &&
            OracleCopySource.NUMBER_SCALE.templateValue(source) == null &&
            Number(source.numberPrecision) < 130) {
            return [{
                context: source.owner,
                messageType: 'unsupported',
                message: () => stringResources.Oracle_PrecisionIsLessThanDefaultScale_ErrorMessage,
                redirectTo: { attribute: OracleCopySource.NUMBER_PRECISION }
            }];
        }
        return [];
    }

    private static validateNumberScale(source: OracleCopySource): IViolation[] {
        // Compare literal values only; expressions are validated separately.
        if (source.numberPrecision != null &&
            OracleCopySource.NUMBER_PRECISION.templateValue(source) == null &&
            source.numberScale != null &&
            OracleCopySource.NUMBER_SCALE.templateValue(source) == null &&
            Number(source.numberScale) > Number(source.numberPrecision)) {
            return [{
                context: source.owner,
                messageType: 'unsupported',
                message: () => stringResources.Oracle_PrecisionIsLessThanScale_ErrorMessage,
                redirectTo: { attribute: OracleCopySource.NUMBER_SCALE }
            }];
        }
        return [];
    }
}
